/**
 * src/components/AdminTrialInvitationsTab.tsx
 *
 * Onglet admin "Essais gratuits" — gère les invitations envoyées aux prospects
 * + les coupons (templates de "nombre de simulations offertes").
 *
 * Cf. spec/trial-mode-with-coupon.md + décision Valérie 27/05/2026.
 *
 * Réservé ADM + COO (les non-autorisés voient un message d'accès interdit
 * côté API, donc inutile de masquer le composant).
 */

import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Loader2, Mail, Send, RotateCw, Ban, Check, X, Plus, Pencil, Trash2 } from "lucide-react";

type Invitation = Awaited<ReturnType<typeof adminApi.listTrialInvitations>>[number];
type Coupon = Awaited<ReturnType<typeof adminApi.listTrialCoupons>>[number];
type Licence = {
  id: string;
  code: string;
  name: string;
  tier?: string | null;
  billingInterval?: string | null;
  zohoPlanCode?: string | null;
  isTrial?: boolean | null;
};

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  sent: { label: "📨 Envoyée", color: "bg-blue-100 text-blue-700" },
  clicked: { label: "👆 Cliquée", color: "bg-indigo-100 text-indigo-700" },
  signup_completed: { label: "✅ Inscrit", color: "bg-green-100 text-green-700" },
  activated: { label: "🚀 Activé", color: "bg-emerald-100 text-emerald-800" },
  declined: { label: "❌ Refusé", color: "bg-rose-100 text-rose-700" },
  expired: { label: "⏰ Expiré", color: "bg-amber-100 text-amber-700" },
  disabled: { label: "🚫 Désactivé", color: "bg-slate-200 text-slate-600" },
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

export function AdminTrialInvitationsTab({
  subTab,
  onSubTabChange,
}: {
  subTab?: "invitations" | "coupons";
  onSubTabChange?: (value: "invitations" | "coupons") => void;
} = {}) {
  // Tabs contrôlés si parent fournit subTab+onSubTabChange (depuis home grid),
  // sinon defaultValue (clic depuis la sidebar).
  const controlled = subTab !== undefined && onSubTabChange !== undefined;
  return (
    <div className="space-y-4">
      <Tabs
        {...(controlled
          ? { value: subTab, onValueChange: (v) => onSubTabChange!(v as "invitations" | "coupons") }
          : { defaultValue: "invitations" })}
        className="w-full"
      >
        <TabsList>
          <TabsTrigger value="invitations">Offres d'essai</TabsTrigger>
          <TabsTrigger value="coupons">Coupons</TabsTrigger>
        </TabsList>
        <TabsContent value="invitations" className="mt-4">
          <InvitationsPanel />
        </TabsContent>
        <TabsContent value="coupons" className="mt-4">
          <CouponsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Panel Invitations ----------

function InvitationsPanel() {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await adminApi.listTrialInvitations(statusFilter === "all" ? undefined : statusFilter);
      setInvitations(rows);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const handleResend = async (id: string) => {
    try {
      await adminApi.resendTrialInvitation(id);
      await load();
    } catch (e: any) { alert(`Échec de la relance : ${e.message}`); }
  };

  const handleDisable = async (id: string) => {
    if (!confirm("Désactiver définitivement cette offre d'essai ? Le prospect ne pourra plus l'utiliser.")) return;
    try {
      await adminApi.disableTrialInvitation(id);
      await load();
    } catch (e: any) { alert(`Échec : ${e.message}`); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm">Filtre :</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white">
          <Plus className="h-4 w-4" /> Nouvelle offre d'essai
        </Button>
      </div>

      {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded">{error}</div>}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
      ) : invitations.length === 0 ? (
        <div className="text-slate-500 text-sm py-8 text-center">Aucune offre d'essai pour ce filtre.</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Envoyée</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Prospect</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Licence</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Coupon</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Statut</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Dernière action</th>
                <th className="px-3 py-2 text-right font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((inv) => {
                const s = STATUS_LABEL[inv.status] || { label: inv.status, color: "bg-slate-100" };
                const lastAction = inv.activatedAt || inv.declinedAt || inv.signedUpAt || inv.clickedAt || inv.lastReminderAt || inv.sentAt;
                const canResend = !inv.disabledAt && inv.status !== "activated" && inv.status !== "declined";
                return (
                  <tr key={inv.id} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(inv.sentAt)}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{[inv.prospectFirstName, inv.prospectName].filter(Boolean).join(" ") || "—"}</div>
                      <div className="text-xs text-slate-500">{inv.prospectEmail}</div>
                    </td>
                    <td className="px-3 py-2">{inv.licenceName || "—"}</td>
                    <td className="px-3 py-2 font-mono text-xs">{inv.trialCouponCode}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${s.color}`}>{s.label}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500 text-xs">{fmtDate(lastAction)}</td>
                    <td className="px-3 py-2 text-right space-x-1">
                      {canResend && (
                        <button onClick={() => handleResend(inv.id)} title="Relancer" className="p-1 rounded hover:bg-slate-200">
                          <RotateCw className="h-4 w-4 text-slate-600" />
                        </button>
                      )}
                      {!inv.disabledAt && (
                        <button onClick={() => handleDisable(inv.id)} title="Désactiver" className="p-1 rounded hover:bg-rose-100">
                          <Ban className="h-4 w-4 text-rose-600" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateInvitationModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Modal de création d'invitation ----------

function CreateInvitationModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [name, setName] = useState("");
  const [licenceId, setLicenceId] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [licences, setLicences] = useState<Licence[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);

  useEffect(() => {
    // Ne propose QUE les licences "à période d'essai" (flag is_trial) et actives.
    // Évite d'envoyer par erreur un prospect sur une licence standard facturée.
    //
    // Le filtre est appliqué CÔTÉ SERVEUR : depuis que /api/admin/tables/:table
    // pagine par défaut (limit=50), filtrer côté client sur une page tronquée
    // ferait disparaître une licence d'essai en silence, sans erreur. Le `limit`
    // explicite couvre largement le catalogue et rend le comportement lisible.
    fetch("/api/admin/tables/licences?f.isActive=eq:true&f.isTrial=eq:true&limit=200")
      .then((r) => r.json())
      .then((d) => {
        setLicences((d.records || []) as unknown as Licence[]);
      })
      .catch(() => setLicences([]));

    adminApi.listTrialCoupons().then((c) => setCoupons(c.filter((x) => x.isActive))).catch(() => setCoupons([]));
  }, []);

  const submit = async () => {
    setError(null);
    if (!email.trim() || !licenceId || !couponCode) {
      setError("Email, licence et coupon sont requis.");
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.createTrialInvitation({
        prospectEmail: email.trim(),
        prospectFirstName: firstName.trim() || undefined,
        prospectName: name.trim() || undefined,
        licenceId,
        trialCouponCode: couponCode,
        notes: notes.trim() || undefined,
      });
      onCreated();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg m-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-[#384872] flex items-center gap-2">
            <Mail className="h-5 w-5" /> Nouvelle offre d'essai
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded">{error}</div>}
          <div>
            <Label>Email du prospect <span className="text-red-500">*</span></Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prospect@entreprise.com" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Prénom du prospect</Label>
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="ex : Camille" className="mt-1" />
            </div>
            <div>
              <Label>Nom du prospect</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : Durand" className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Licence d'essai <span className="text-red-500">*</span></Label>
            <Select value={licenceId} onValueChange={setLicenceId}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Choisir une licence trial" /></SelectTrigger>
              <SelectContent>
                {licences.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">Seules les licences cochées « Période d'essai » apparaissent ici.</p>
          </div>
          <div>
            <Label>Coupon (nb de sessions offertes) <span className="text-red-500">*</span></Label>
            <Select value={couponCode} onValueChange={setCouponCode}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Choisir un coupon" /></SelectTrigger>
              <SelectContent>
                {coupons.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.code} — {c.trialSessionsLimit} sessions
                    {c.maxUses != null && ` (${c.usesCount}/${c.maxUses})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes internes (optionnel)</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full mt-1 px-3 py-2 border rounded-md text-sm" placeholder="ex: rencontré au Salon Sales 03/26" />
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Envoyer l'offre d'essai
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Panel Coupons (CRUD simple) ----------

function CouponsPanel() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try { setCoupons(await adminApi.listTrialCoupons()); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (c: Coupon) => {
    try { await adminApi.updateTrialCoupon(c.code, { isActive: !c.isActive }); await load(); }
    catch (e: any) { alert(e.message); }
  };

  const handleDelete = async (c: Coupon) => {
    if (!confirm(`Supprimer définitivement le coupon "${c.code}" ?`)) return;
    try {
      await adminApi.deleteTrialCoupon(c.code);
      await load();
    } catch (e: any) {
      // 409 : le coupon est utilisé par des offres d'essai (FK RESTRICT).
      // On propose la désactivation comme fallback.
      const msg = String(e.message || "");
      if (msg.includes("est utilisé par")) {
        if (confirm(`${msg}\n\nVoulez-vous le désactiver à la place ?`)) {
          try { await adminApi.updateTrialCoupon(c.code, { isActive: false }); await load(); }
          catch (e2: any) { alert(e2.message); }
        }
      } else {
        alert(msg);
      }
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Les coupons définissent le <strong>nombre de simulations offertes</strong>. Crée
          quelques templates réutilisables (3, 5, 10 sessions…) — pas besoin d'un coupon par prospect.
        </p>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white">
          <Plus className="h-4 w-4" /> Nouveau coupon
        </Button>
      </div>

      {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded">{error}</div>}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
      ) : coupons.length === 0 ? (
        <div className="text-slate-500 text-sm py-8 text-center">Aucun coupon créé pour le moment.</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Code</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Sessions</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Usage</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Expiration</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Note</th>
                <th className="px-3 py-2 text-left font-medium text-slate-600">Statut</th>
                <th className="px-3 py-2 text-right font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.code} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                  <td className="px-3 py-2">{c.trialSessionsLimit}</td>
                  <td className="px-3 py-2">{c.usesCount}{c.maxUses != null ? ` / ${c.maxUses}` : ""}</td>
                  <td className="px-3 py-2">{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString("fr-FR") : "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{c.note || "—"}</td>
                  <td className="px-3 py-2">
                    {c.isActive
                      ? <span className="text-green-700 inline-flex items-center gap-1 text-xs"><Check className="h-3 w-3" /> Actif</span>
                      : <span className="text-slate-500 inline-flex items-center gap-1 text-xs"><Ban className="h-3 w-3" /> Inactif</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditingCoupon(c)} title="Modifier" className="p-1 rounded hover:bg-slate-100">
                        <Pencil className="h-4 w-4 text-[#384872]" />
                      </button>
                      {c.isActive ? (
                        <button onClick={() => handleDelete(c)} title="Supprimer (désactiver)" className="p-1 rounded hover:bg-rose-100">
                          <Trash2 className="h-4 w-4 text-rose-600" />
                        </button>
                      ) : (
                        <button onClick={() => toggleActive(c)} title="Réactiver" className="p-1 rounded hover:bg-slate-100">
                          <RotateCw className="h-4 w-4 text-[#384872]" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateCouponModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {editingCoupon && (
        <EditCouponModal
          coupon={editingCoupon}
          onClose={() => setEditingCoupon(null)}
          onSaved={() => { setEditingCoupon(null); load(); }}
        />
      )}
    </div>
  );
}

function CreateCouponModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState("");
  const [sessions, setSessions] = useState("5");
  const [maxUses, setMaxUses] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const sessionsNum = Number(sessions);
    if (!code.trim() || !Number.isInteger(sessionsNum) || sessionsNum < 1) {
      setError("Code et nombre de sessions (≥ 1) requis.");
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.createTrialCoupon({
        code: code.trim(),
        trialSessionsLimit: sessionsNum,
        maxUses: maxUses.trim() ? Number(maxUses) : null,
        expiresAt: expiresAt || undefined,
        note: note.trim() || undefined,
      });
      onCreated();
    } catch (e: any) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-[#384872]">Nouveau coupon d'essai</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded">{error}</div>}
          <div>
            <Label>Code <span className="text-red-500">*</span></Label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ex: ESSAI-5-SESSIONS" className="mt-1 font-mono" />
          </div>
          <div>
            <Label>Nombre de simulations offertes <span className="text-red-500">*</span></Label>
            <Input type="number" min="1" value={sessions} onChange={(e) => setSessions(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>Usages max (optionnel, vide = illimité)</Label>
            <Input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="ex: 50" className="mt-1" />
          </div>
          <div>
            <Label>Expiration (optionnel)</Label>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>Note interne (optionnel)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: campagne Q3 2026" className="mt-1" />
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Créer le coupon
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditCouponModal({ coupon, onClose, onSaved }: { coupon: Coupon; onClose: () => void; onSaved: () => void }) {
  const [maxUses, setMaxUses] = useState(coupon.maxUses != null ? String(coupon.maxUses) : "");
  const [expiresAt, setExpiresAt] = useState(
    coupon.expiresAt ? new Date(coupon.expiresAt).toISOString().slice(0, 10) : "",
  );
  const [note, setNote] = useState(coupon.note || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const maxUsesNum = maxUses.trim() ? Number(maxUses) : null;
    if (maxUsesNum != null && (!Number.isInteger(maxUsesNum) || maxUsesNum < 1)) {
      setError("Usages max doit être un entier ≥ 1 (ou vide pour illimité).");
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.updateTrialCoupon(coupon.code, {
        maxUses: maxUsesNum,
        expiresAt: expiresAt || null,
        note: note.trim() || null,
      });
      onSaved();
    } catch (e: any) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-[#384872]">Modifier le coupon</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100"><X className="h-5 w-5 text-slate-500" /></button>
        </div>
        <div className="px-6 py-4 space-y-3">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded">{error}</div>}
          <div>
            <Label>Code <span className="text-slate-400 text-xs font-normal">(non modifiable)</span></Label>
            <Input value={coupon.code} readOnly className="mt-1 font-mono bg-slate-100" />
          </div>
          <div>
            <Label>Nombre de simulations offertes <span className="text-slate-400 text-xs font-normal">(non modifiable)</span></Label>
            <Input value={coupon.trialSessionsLimit} readOnly className="mt-1 bg-slate-100" />
          </div>
          <div>
            <Label>Usages max (optionnel, vide = illimité)</Label>
            <Input type="number" min="1" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} placeholder="ex: 50" className="mt-1" />
          </div>
          <div>
            <Label>Expiration (optionnel)</Label>
            <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>Note interne (optionnel)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: campagne Q3 2026" className="mt-1" />
          </div>
        </div>
        <div className="px-6 py-3 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="gap-2 bg-[#384872] hover:bg-[#2d3a5c] text-white">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}
